'use client'

import { useMemo } from 'react'
import useSWR, {
  unstable_serialize,
  useSWRConfig,
  type SWRConfiguration,
  type SWRResponse,
} from 'swr'
import type { ZodType } from 'zod'

import {
  resolvePolicy,
  type CachePolicyInput,
} from '@/core/lib/cache/cache.policy'
import type { FetchError, FetcherResponse } from '@/core/lib/fetcher/core'

import { getSwrRequestPolicy, swrFetcher } from './swr.client'
import { buildSwrCacheKey, type SwrCacheKey, type SwrKey } from './swr.key'

type UseCachedSWROptions<T> = SWRConfiguration<
  FetcherResponse<T>,
  FetchError
> & { responseSchema?: ZodType<T> }

export function useCachedSWR<T>(
  key: SwrKey | null,
  policy: CachePolicyInput = 'REALTIME',
  override?: UseCachedSWROptions<T>
): SWRResponse<FetcherResponse<T>, FetchError> {
  const inheritedConfig = useSWRConfig()
  const { cache, fallback } = inheritedConfig
  const { responseSchema, ...swrOverride }: UseCachedSWROptions<T> =
    override ?? {}
  // 서버와 같은 복합 키만 fallback에 매칭된다. null 키는 요청하지 않는다.
  const cacheKey = useMemo<SwrCacheKey | null>(
    () => (key === null ? null : buildSwrCacheKey(key, policy)),
    [key, policy]
  )

  const resolved = resolvePolicy(policy)
  const serializedKey = cacheKey === null ? null : unstable_serialize(cacheKey)
  const cachedData =
    serializedKey === null ? undefined : cache.get(serializedKey)?.data
  // SWR와 같은 우선순위: hook fallbackData → provider fallbackData → 병합 fallback 맵.
  // null도 명시적인 값이므로 ??로 뒤의 성공 응답으로 바꾸지 않는다.
  const hookConfig = { ...resolved.swr, ...swrOverride }
  const initialConfig = { ...inheritedConfig, ...hookConfig }
  let fallbackData =
    serializedKey === null ? undefined : fallback?.[serializedKey]
  if (
    serializedKey !== null &&
    hookConfig.fallback &&
    Object.hasOwn(hookConfig.fallback, serializedKey)
  ) {
    fallbackData = hookConfig.fallback[serializedKey]
  }
  if (initialConfig.fallbackData !== undefined) {
    fallbackData = initialConfig.fallbackData
  }
  const hasHydratedData =
    cachedData === undefined &&
    typeof fallbackData === 'object' &&
    fallbackData !== null &&
    'ok' in fallbackData &&
    fallbackData.ok === true

  // 실제 캐시 값은 기존 stale/mount 정책을 유지한다. miss면 STATIC도 첫 요청한다.
  let revalidateOnMount = resolved.swr?.revalidateOnMount
  const policyMountOverride =
    typeof policy === 'string' ? undefined : policy.swr?.revalidateOnMount
  if (cachedData === undefined && policyMountOverride === undefined) {
    revalidateOnMount = !hasHydratedData
  }
  if (swrOverride.revalidateOnMount !== undefined) {
    revalidateOnMount = swrOverride.revalidateOnMount
  }

  const config: SWRConfiguration<FetcherResponse<T>, FetchError> = {
    ...hookConfig,
    revalidateOnMount,
    shouldRetryOnError: false,
    errorRetryCount: 0,
  }

  return useSWR<FetcherResponse<T>, FetchError, SwrCacheKey | null>(
    cacheKey,
    ([resolvedKey, resolvedPolicy]: SwrCacheKey) =>
      swrFetcher<T>(
        resolvedKey,
        resolvedPolicy,
        getSwrRequestPolicy(resolvedKey),
        responseSchema
      ),
    config
  )
}
